/**
 * TestCaseStepsTab — step builder migrated from TestCaseDrawer.
 * Manages an ordered list of (action, expectedResult) pairs with
 * add/remove/update operations and AI step generation.
 */

import { useState } from 'react';
import { Plus, Trash2, Sparkles, Loader2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';

const API_URL = API_BASE_URL;

export interface IStepDraft {
    localId:        string;
    action:         string;
    expectedResult: string;
}

let _counter = 0;
export function newLocalId(): string { return `step-${++_counter}`; }
export function buildEmptyStep(): IStepDraft { return { localId: newLocalId(), action: '', expectedResult: '' }; }

const CELL_CLASS =
    'w-full px-2 py-1.5 text-xs border border-slate-200 dark:border-gh-border-dark rounded-lg ' +
    'bg-white dark:bg-gh-bg-dark text-slate-800 dark:text-slate-200 ' +
    'placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition resize-none ' +
    'min-h-[56px] overflow-hidden';

/** Auto-grow a textarea to fit its content. */
function autoGrow(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    el.style.height = `${Math.max(56, el.scrollHeight)}px`;
}

interface TestCaseStepsTabProps {
    steps:    IStepDraft[];
    onChange: (steps: IStepDraft[]) => void;
}

export function TestCaseStepsTab({ steps, onChange }: TestCaseStepsTabProps) {
    const { token } = useAuth();

    const [aiPanelOpen,   setAiPanelOpen]   = useState(false);
    const [aiPrompt,      setAiPrompt]      = useState('');
    const [isGenerating,  setIsGenerating]  = useState(false);
    const [aiError,       setAiError]       = useState<string | null>(null);

    function addStep() {
        onChange([...steps, buildEmptyStep()]);
    }

    function removeStep(localId: string) {
        const next = steps.filter((s) => s.localId !== localId);
        onChange(next.length > 0 ? next : [buildEmptyStep()]);
    }

    function updateStep(localId: string, field: 'action' | 'expectedResult', value: string) {
        onChange(steps.map((s) => s.localId === localId ? { ...s, [field]: value } : s));
    }

    async function handleGenerateSteps() {
        if (!aiPrompt.trim()) return;
        setIsGenerating(true);
        setAiError(null);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/ai/generate-test-steps`,
                { prompt: aiPrompt.trim() },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!data.success) { setAiError(data.error ?? 'AI generation failed.'); return; }
            const generated: IStepDraft[] = (
                data.data.steps as Array<{ action: string; expectedResult: string }>
            ).map((s) => ({ localId: newLocalId(), action: s.action, expectedResult: s.expectedResult }));
            onChange(generated.length > 0 ? generated : [buildEmptyStep()]);
            setAiPanelOpen(false);
            setAiPrompt('');
        } catch (err: unknown) {
            const msg = axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : 'AI generation failed.';
            setAiError(msg);
        } finally {
            setIsGenerating(false);
        }
    }

    return (
        <div data-testid="test-case-steps-tab" className="flex flex-col gap-3 p-4 overflow-y-auto">
            {/* AI Panel toggle */}
            <button
                data-testid="steps-ai-toggle"
                aria-expanded={aiPanelOpen}
                aria-controls="steps-ai-panel"
                onClick={() => setAiPanelOpen((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 hover:underline self-start"
            >
                <Sparkles size={13} />
                Generate with AI
                {aiPanelOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>

            {aiPanelOpen && (
                <div id="steps-ai-panel" data-testid="steps-ai-panel" className="border border-violet-200 dark:border-violet-800 rounded-xl p-3 bg-violet-50/50 dark:bg-violet-900/10">
                    <textarea
                        rows={2}
                        aria-label="Describe the feature to test"
                        data-testid="steps-ai-prompt"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder="Describe the feature to test…"
                        className={CELL_CLASS + ' mb-2'}
                    />
                    {aiError && (
                        <div className="flex items-center gap-1 text-xs text-rose-500 mb-2">
                            <AlertCircle size={12} /> {aiError}
                        </div>
                    )}
                    <button
                        data-testid="steps-generate-button"
                        onClick={handleGenerateSteps}
                        disabled={!aiPrompt.trim() || isGenerating}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white rounded-lg disabled:opacity-50 transition-colors"
                    >
                        {isGenerating
                            ? <><Loader2 size={12} className="animate-spin" /> Generating…</>
                            : <><Sparkles size={12} /> Generate steps</>
                        }
                    </button>
                </div>
            )}

            {/* Steps */}
            <div className="flex flex-col gap-2">
                {steps.map((step, i) => (
                    <div key={step.localId} className="flex gap-2 items-start">
                        <span className="text-xs font-mono text-slate-400 w-5 flex-shrink-0 mt-2 text-right">{i + 1}.</span>
                        <div className="flex-1 flex flex-col gap-1">
                            <textarea
                                rows={2}
                                aria-label={`Step ${i + 1} action`}
                                data-testid={`step-action-${step.localId}`}
                                value={step.action}
                                onChange={(e) => { updateStep(step.localId, 'action', e.target.value); autoGrow(e.target); }}
                                onFocus={(e) => autoGrow(e.target)}
                                ref={(el) => { if (el) autoGrow(el); }}
                                placeholder="Action"
                                className={CELL_CLASS}
                            />
                            <textarea
                                rows={2}
                                aria-label={`Step ${i + 1} expected result`}
                                data-testid={`step-result-${step.localId}`}
                                value={step.expectedResult}
                                onChange={(e) => { updateStep(step.localId, 'expectedResult', e.target.value); autoGrow(e.target); }}
                                onFocus={(e) => autoGrow(e.target)}
                                ref={(el) => { if (el) autoGrow(el); }}
                                placeholder="Expected result"
                                className={CELL_CLASS}
                            />
                        </div>
                        <button
                            data-testid={`step-remove-${step.localId}`}
                            aria-label={`Remove step ${i + 1}`}
                            onClick={() => removeStep(step.localId)}
                            className="mt-2 p-1 rounded text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors flex-shrink-0"
                            title="Remove step"
                        >
                            <Trash2 size={13} />
                        </button>
                    </div>
                ))}
            </div>

            <button
                data-testid="steps-add-button"
                onClick={addStep}
                className="flex items-center gap-1.5 self-start text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
                <Plus size={13} /> Add step
            </button>
        </div>
    );
}
